import PropTypes from 'prop-types';

import Checkbox from '#globalComponents/Checkbox/Checkbox';

import ColumnResizeHandle from './ColumnResizeHandle';
import SortIndicator from './SortIndicator';
import {
	ALIGN_CLASS,
	columnStyle,
	HEADER_CELL,
	JUSTIFY_CLASS,
	SELECTION_COLUMN_WIDTH,
	stickyEdgeClass,
} from './tableStyles';

const STICKY_HEADER_CELL = 'sticky top-0 z-20 bg-bgBase-300';
const STICKY_CORNER_CELL = 'sticky top-0 z-30 bg-bgBase-300';

const HeaderLabel = ({ column, sort, isSorted }) => (
	<span className={`inline-flex items-center gap-1 ${JUSTIFY_CLASS[column.align] ?? ''}`}>
		{column.sortable && sort ? (
			<button
				type="button"
				onClick={() => sort.onSortChange(column.key)}
				className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-primary-600 ${
					isSorted ? 'text-primary-600' : ''
				}`}
			>
				{column.label}
				<SortIndicator active={isSorted} direction={isSorted ? sort.sortDir : undefined} />
			</button>
		) : (
			column.label
		)}
		{column.headerAccessory}
	</span>
);

HeaderLabel.propTypes = {
	column: PropTypes.object.isRequired,
	sort: PropTypes.object,
	isSorted: PropTypes.bool,
};

const TableHead = ({ columns, selection, sort, scrolls, tableId, firstColumnResize }) => (
	<thead>
		<tr className="border-b border-bgBase-500">
			{selection && (
				<th
					scope="col"
					className={`${STICKY_CORNER_CELL} left-0 ${HEADER_CELL}`}
					style={{ width: SELECTION_COLUMN_WIDTH }}
				>
					<Checkbox
						id={`${tableId}-select-all`}
						checked={selection.allChecked}
						showDash={selection.someChecked}
						onChange={selection.onToggleAll}
						aria-label="Alle Zeilen auswählen"
					/>
				</th>
			)}
			{columns.map((column, index) => {
				const isSorted = sort?.sortKey === column.key;

				return (
					<th
						key={column.key}
						scope="col"
						aria-sort={isSorted ? (sort.sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
						className={`${column.sticky ? STICKY_CORNER_CELL : STICKY_HEADER_CELL} ${HEADER_CELL} ${
							ALIGN_CLASS[column.align] ?? ALIGN_CLASS.left
						} ${stickyEdgeClass(column.sticky, scrolls)} ${column.headerClassName ?? ''}`}
						style={columnStyle(columns, index, Boolean(selection))}
					>
						<HeaderLabel column={column} sort={sort} isSorted={isSorted} />
						{index === 0 && <ColumnResizeHandle {...firstColumnResize} />}
					</th>
				);
			})}
		</tr>
	</thead>
);

TableHead.propTypes = {
	columns: PropTypes.array.isRequired,
	selection: PropTypes.shape({
		allChecked: PropTypes.bool.isRequired,
		someChecked: PropTypes.bool.isRequired,
		onToggleAll: PropTypes.func.isRequired,
	}),
	scrolls: PropTypes.bool,
	sort: PropTypes.shape({
		sortKey: PropTypes.string,
		sortDir: PropTypes.oneOf(['asc', 'desc']),
		onSortChange: PropTypes.func.isRequired,
	}),
	tableId: PropTypes.string.isRequired,
	firstColumnResize: PropTypes.shape({
		onResize: PropTypes.func.isRequired,
		onResizeEnd: PropTypes.func.isRequired,
		onReset: PropTypes.func.isRequired,
	}).isRequired,
};

export default TableHead;
